// Print utilities for Admin Dashboard
// Contains all print functions for invoices, receipts, quotes, and client sheets

import { COMPANY_INFO } from './constants';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * REAL PDF DOWNLOAD: fetches an invoice PDF from the backend (ReportLab) and triggers a download.
 * @param {Object} invoice - Must have .id
 */
export const downloadInvoicePdf = async (invoice) => {
  try {
    const token = localStorage.getItem('token');
    const res = await fetch(`${API_BASE}/admin/invoices/${invoice.id}/pdf`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
      throw new Error(`HTTP ${res.status}`);
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Facture_${invoice.invoice_number || invoice.id.slice(0, 8)}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch (e) {
    console.error('downloadInvoicePdf failed', e);
    alert('Impossible de télécharger la facture. Veuillez réessayer.');
  }
};

/**
 * REAL PDF DOWNLOAD for a professional quote.
 */
export const downloadQuotePdf = async (quote) => {
  try {
    const token = localStorage.getItem('token');
    const res = await fetch(`${API_BASE}/admin/quotes/${quote.id}/pdf`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Devis_${quote.quote_number || quote.id.slice(0, 8)}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch (e) {
    console.error('downloadQuotePdf failed', e);
    alert('Impossible de télécharger le devis. Veuillez réessayer.');
  }
};

/**
 * Safe print wrapper that works on all devices including iOS Safari.
 * Uses Blob URL approach which works better than document.write on mobile.
 */
const safePrintOpen = (htmlContent) => {
  // Remove any existing print iframe
  const existingFrame = document.getElementById('print-fallback-frame');
  if (existingFrame) existingFrame.remove();
  const existingBtn = document.getElementById('print-close-btn');
  if (existingBtn) existingBtn.remove();

  // Try Blob URL approach first (works on most modern browsers including iOS Safari)
  try {
    const blob = new Blob([htmlContent], { type: 'text/html; charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const newWindow = window.open(url, '_blank');
    if (newWindow) {
      // Clean up blob URL after a delay
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      return;
    }
  } catch (e) {
    // Blob approach failed, fall through to iframe
  }

  // Fallback: use a full-screen iframe (for when popups are blocked)
  const iframe = document.createElement('iframe');
  iframe.id = 'print-fallback-frame';
  iframe.style.cssText = 'position:fixed;top:0;left:0;width:100%;height:100%;z-index:99999;border:none;background:white;';
  document.body.appendChild(iframe);
  const doc = iframe.contentDocument || iframe.contentWindow.document;
  doc.open();
  doc.write(htmlContent);
  doc.close();
  // Add close button
  const closeBtn = document.createElement('button');
  closeBtn.id = 'print-close-btn';
  closeBtn.textContent = 'Fermer';
  closeBtn.style.cssText = 'position:fixed;top:10px;right:10px;z-index:100000;background:#dc2626;color:white;border:none;padding:12px 24px;border-radius:8px;font-size:16px;cursor:pointer;font-weight:bold;box-shadow:0 4px 12px rgba(0,0,0,0.3);';
  closeBtn.onclick = () => { iframe.remove(); closeBtn.remove(); };
  document.body.appendChild(closeBtn);
};

/**
 * Download/Print an invoice as PDF
 * @param {Object} invoice - Invoice object with all details
 */
export const downloadInvoice = (invoice) => {
  const logoUrl = COMPANY_INFO.logoUrl;
  const invoiceHTML = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Facture ${invoice.invoice_number}</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: 'Segoe UI', Arial, sans-serif; padding: 40px; color: #333; background: #fff; }
        .header { display: flex; justify-content: space-between; margin-bottom: 30px; border-bottom: 3px solid #dc2626; padding-bottom: 20px; }
        .logo-section { }
        .logo-img { height: 80px; }
        .invoice-info { text-align: right; }
        .invoice-number { font-size: 22px; font-weight: bold; color: #dc2626; }
        .invoice-date { color: #666; margin-top: 5px; font-size: 13px; }
        .addresses { display: flex; justify-content: space-between; margin-bottom: 30px; margin-top: 30px; }
        .address-block { width: 45%; }
        .address-block h3 { color: #dc2626; margin-bottom: 10px; font-size: 12px; text-transform: uppercase; letter-spacing: 1px; }
        .address-block p { line-height: 1.6; font-size: 13px; }
        table { width: 100%; border-collapse: collapse; margin-bottom: 30px; }
        th { background: #f3f4f6; padding: 12px; text-align: left; font-weight: 600; border-bottom: 2px solid #dc2626; font-size: 12px; }
        td { padding: 12px; border-bottom: 1px solid #e5e7eb; font-size: 13px; }
        .text-right { text-align: right; }
        .totals { width: 300px; margin-left: auto; }
        .totals tr td { padding: 8px 12px; }
        .totals .total-row { font-weight: bold; font-size: 16px; background: #dc2626; color: white; }
        .footer { margin-top: 50px; padding-top: 20px; border-top: 1px solid #e5e7eb; text-align: center; color: #666; font-size: 10px; }
        .status { display: inline-block; padding: 6px 16px; border-radius: 20px; font-size: 11px; font-weight: bold; text-transform: uppercase; }
        .status-paid { background: #22c55e; color: white; }
        .status-sent { background: #3b82f6; color: white; }
        .status-draft { background: #71717a; color: white; }
        .notes { background: #f9fafb; padding: 15px; border-radius: 8px; margin-top: 20px; font-style: italic; color: #666; font-size: 12px; }
        .bank-info { background: #f0f0f0; padding: 15px; border-radius: 8px; margin-top: 20px; font-size: 11px; }
        .bank-info strong { color: #dc2626; }
        @media print { body { padding: 20px; } }
      </style>
    </head>
    <body>
      <div class="header">
        <div class="logo-section">
          <img src="${logoUrl}" alt="Le Hub Atelier Auto Moto" class="logo-img" />
        </div>
        <div class="invoice-info">
          <div class="invoice-number">${invoice.invoice_number}</div>
          <div class="invoice-date">Date: ${new Date(invoice.created_at).toLocaleDateString('fr-FR')}</div>
          ${invoice.due_date ? `<div class="invoice-date">Échéance: ${new Date(invoice.due_date).toLocaleDateString('fr-FR')}</div>` : ''}
          <div style="margin-top: 10px;">
            <span class="status status-${invoice.status}">${invoice.status === 'paid' ? 'PAYÉE' : invoice.status === 'sent' ? 'À PAYER' : 'BROUILLON'}</span>
          </div>
        </div>
      </div>

      <div class="addresses">
        <div class="address-block">
          <h3>Émetteur</h3>
          <p>
            <strong>${COMPANY_INFO.name}</strong><br>
            ${COMPANY_INFO.address}<br>
            SIRET: ${COMPANY_INFO.siret}<br>
            TVA Intracom.: ${COMPANY_INFO.tva}<br>
            Tél: ${COMPANY_INFO.phone}<br>
            ${COMPANY_INFO.email}<br>
            www.lehubatelier.fr
          </p>
        </div>
        <div class="address-block">
          <h3>Client</h3>
          <p>
            ${invoice.user_company ? `<strong>${invoice.user_company}</strong><br>` : ''}
            <strong>${invoice.user_firstname ? invoice.user_firstname + ' ' : ''}${invoice.user_name}</strong><br>
            ${invoice.user_address ? invoice.user_address.replace(/\\n/g, '<br>') + '<br>' : ''}
            ${invoice.user_phone ? `Tél: ${invoice.user_phone}<br>` : ''}
            ${invoice.user_email}
          </p>
        </div>
      </div>

      <table>
        <thead>
          <tr>
            <th>Description</th>
            <th class="text-right">Quantité</th>
            <th class="text-right">Prix unitaire HT</th>
            <th class="text-right">Total HT</th>
          </tr>
        </thead>
        <tbody>
          ${invoice.items.map(item => `
            <tr>
              <td>${item.description}</td>
              <td class="text-right">${item.quantity}</td>
              <td class="text-right">${item.unit_price.toFixed(2)} €</td>
              <td class="text-right">${item.total.toFixed(2)} €</td>
            </tr>
          `).join('')}
        </tbody>
      </table>

      <table class="totals">
        <tr>
          <td>Sous-total HT</td>
          <td class="text-right">${invoice.subtotal.toFixed(2)} €</td>
        </tr>
        <tr>
          <td>TVA (${invoice.tax_rate}%)</td>
          <td class="text-right">${invoice.tax_amount.toFixed(2)} €</td>
        </tr>
        <tr class="total-row">
          <td>Total TTC</td>
          <td class="text-right">${invoice.total.toFixed(2)} €</td>
        </tr>
      </table>

      ${invoice.notes ? `<div class="notes"><strong>Notes:</strong> ${invoice.notes}</div>` : ''}

      <div class="bank-info">
        <strong>Coordonnées bancaires pour règlement :</strong><br>
        IBAN: FR76 1010 7001 1100 3180 8375 451<br>
        BIC: BREDFRPPXXX
      </div>

      ${invoice.status === 'paid' && invoice.paid_date ? `
        <p style="margin-top: 20px; color: #22c55e; font-weight: bold;">
          ✓ Facture payée le ${new Date(invoice.paid_date).toLocaleDateString('fr-FR')}
        </p>
      ` : ''}

      <div class="footer">
        <p><strong>${COMPANY_INFO.name}</strong> - SIRET: ${COMPANY_INFO.siret} - TVA Intracommunautaire: ${COMPANY_INFO.tva}</p>
        <p style="margin-top: 5px;">www.lehubatelier.fr - Tél: ${COMPANY_INFO.phone} - ${COMPANY_INFO.email}</p>
        <p style="margin-top: 10px;">Merci pour votre confiance !</p>
      </div>

      <script>
        window.onload = function() { window.print(); }
      </script>
    </body>
    </html>
  `;
  safePrintOpen(invoiceHTML);
};

/**
 * Print thermal receipt for cash register sale
 * @param {Object} sale - Sale object
 */
export const printThermalReceipt = (sale) => {
  const htmlContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Ticket - ${sale.sale_number}</title>
      <style>
        @page { size: 80mm auto; margin: 0; }
        * { box-sizing: border-box; }
        body { 
          font-family: 'Courier New', monospace; 
          font-size: 11px; 
          width: 72mm;
          padding: 2mm;
          margin: 0 auto;
          line-height: 1.3;
        }
        .header { text-align: center; margin-bottom: 3mm; }
        .header h1 { font-size: 14px; margin: 0; font-weight: bold; }
        .header p { margin: 1mm 0; font-size: 9px; }
        .line { border-top: 1px dashed #000; margin: 2mm 0; }
        .double-line { border-top: 2px solid #000; margin: 2mm 0; }
        .info { font-size: 10px; }
        .info p { margin: 1mm 0; }
        .items { margin: 2mm 0; }
        .item { margin: 2mm 0; }
        .item-name { font-size: 10px; }
        .item-detail { display: flex; justify-content: space-between; font-size: 10px; }
        .subtotals { font-size: 10px; }
        .subtotals .row { display: flex; justify-content: space-between; margin: 1mm 0; }
        .total { font-weight: bold; font-size: 14px; display: flex; justify-content: space-between; margin: 2mm 0; }
        .payment { font-size: 10px; margin: 2mm 0; }
        .footer { text-align: center; margin-top: 4mm; font-size: 9px; }
        @media print { 
          body { width: 72mm; }
          @page { size: 80mm auto; margin: 0; }
        }
      </style>
    </head>
    <body>
      <div class="header">
        <img src="${COMPANY_INFO.logoUrl}" style="width: 50mm; margin-bottom: 2mm;" alt="Le Hub Atelier"/>
        <p>${COMPANY_INFO.name}</p>
        <p>142 boulevard Murat</p>
        <p>75016 Paris</p>
        <p>Tél: ${COMPANY_INFO.phone}</p>
        <p>Email: ${COMPANY_INFO.email}</p>
        <p>SIRET: ${COMPANY_INFO.siret}</p>
        <p>TVA: ${COMPANY_INFO.tva}</p>
      </div>
      <div class="double-line"></div>
      <div class="info">
        <p><strong>Ticket N°:</strong> ${sale.sale_number}</p>
        <p><strong>Date:</strong> ${new Date(sale.created_at).toLocaleString('fr-FR')}</p>
        <p><strong>Caissier:</strong> ${sale.cashier_name}</p>
        <p><strong>Client:</strong> ${sale.client_name || 'Client comptoir'}</p>
        ${sale.client_phone ? `<p><strong>Tél:</strong> ${sale.client_phone}</p>` : ''}
        ${sale.vehicle_plate ? `<p><strong>Véhicule:</strong> ${sale.vehicle_plate} ${sale.vehicle_model || ''}</p>` : ''}
      </div>
      <div class="line"></div>
      <div class="items">
        ${sale.items.map(item => `
          <div class="item">
            <div class="item-name">${item.description}</div>
            <div class="item-detail">
              <span>${item.quantity} x ${item.unit_price?.toFixed(2) || (item.total / item.quantity).toFixed(2)}€</span>
              <span>${item.total.toFixed(2)}€</span>
            </div>
          </div>
        `).join('')}
      </div>
      <div class="line"></div>
      <div class="subtotals">
        <div class="row"><span>Sous-total HT</span><span>${(sale.total / 1.2).toFixed(2)}€</span></div>
        <div class="row"><span>TVA 20%</span><span>${(sale.total - sale.total / 1.2).toFixed(2)}€</span></div>
      </div>
      <div class="double-line"></div>
      <div class="total">
        <span>TOTAL TTC</span>
        <span>${sale.total.toFixed(2)}€</span>
      </div>
      <div class="line"></div>
      <div class="payment">
        <p><strong>Règlement:</strong> ${sale.payment_method === 'cash' ? 'Espèces' : sale.payment_method === 'card' ? 'Carte bancaire' : sale.payment_method === 'check' ? 'Chèque' : sale.payment_method === 'transfer' ? 'Virement' : 'Mixte'}</p>
        ${sale.cash_amount > 0 ? `<p>Espèces: ${sale.cash_amount.toFixed(2)}€</p>` : ''}
        ${sale.card_amount > 0 ? `<p>CB: ${sale.card_amount.toFixed(2)}€</p>` : ''}
        ${sale.check_amount > 0 ? `<p>Chèque: ${sale.check_amount.toFixed(2)}€</p>` : ''}
        ${sale.transfer_amount > 0 ? `<p>Virement: ${sale.transfer_amount.toFixed(2)}€</p>` : ''}
        ${sale.cash_given ? `<p>Donné: ${sale.cash_given.toFixed(2)}€</p><p>Rendu: ${sale.cash_change.toFixed(2)}€</p>` : ''}
      </div>
      <div class="double-line"></div>
      <div class="footer">
        <p>Merci de votre visite !</p>
        <p>À très bientôt</p>
        <p>---</p>
        <p>TVA non récupérable</p>
      </div>
      <script>
        window.onload = function() { 
          setTimeout(function() { window.print(); }, 100);
        }
      </script>
    </body>
    </html>
  `;
  safePrintOpen(htmlContent);
};

/**
 * Print DUPLICATA of thermal receipt for cash register sale
 * @param {Object} sale - Sale object
 */
export const printDuplicataReceipt = (sale) => {
  const htmlContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>DUPLICATA - ${sale.sale_number}</title>
      <style>
        @page { size: 80mm auto; margin: 0; }
        * { box-sizing: border-box; }
        body { 
          font-family: 'Courier New', monospace; 
          font-size: 11px; 
          width: 72mm;
          padding: 2mm;
          margin: 0 auto;
          line-height: 1.3;
        }
        .duplicata-banner {
          background: #000;
          color: #fff;
          text-align: center;
          padding: 3mm;
          margin-bottom: 3mm;
          font-size: 14px;
          font-weight: bold;
          letter-spacing: 2px;
        }
        .header { text-align: center; margin-bottom: 3mm; }
        .header h1 { font-size: 14px; margin: 0; font-weight: bold; }
        .header p { margin: 1mm 0; font-size: 9px; }
        .line { border-top: 1px dashed #000; margin: 2mm 0; }
        .double-line { border-top: 2px solid #000; margin: 2mm 0; }
        .info { font-size: 10px; }
        .info p { margin: 1mm 0; }
        .items { margin: 2mm 0; }
        .item { margin: 2mm 0; }
        .item-name { font-size: 10px; }
        .item-detail { display: flex; justify-content: space-between; font-size: 10px; }
        .subtotals { font-size: 10px; }
        .subtotals .row { display: flex; justify-content: space-between; margin: 1mm 0; }
        .total { font-weight: bold; font-size: 14px; display: flex; justify-content: space-between; margin: 2mm 0; }
        .payment { font-size: 10px; margin: 2mm 0; }
        .footer { text-align: center; margin-top: 4mm; font-size: 9px; }
        .duplicata-footer { text-align: center; margin-top: 3mm; padding: 2mm; border: 1px dashed #000; font-size: 8px; }
        @media print { 
          body { width: 72mm; }
          @page { size: 80mm auto; margin: 0; }
        }
      </style>
    </head>
    <body>
      <div class="duplicata-banner">*** DUPLICATA ***</div>
      <div class="header">
        <img src="${COMPANY_INFO.logoUrl}" style="width: 50mm; margin-bottom: 2mm;" alt="Le Hub Atelier"/>
        <p>${COMPANY_INFO.name}</p>
        <p>142 boulevard Murat</p>
        <p>75016 Paris</p>
        <p>Tél: ${COMPANY_INFO.phone}</p>
        <p>Email: ${COMPANY_INFO.email}</p>
        <p>SIRET: ${COMPANY_INFO.siret}</p>
        <p>TVA: ${COMPANY_INFO.tva}</p>
      </div>
      <div class="double-line"></div>
      <div class="info">
        <p><strong>Ticket N°:</strong> ${sale.sale_number}</p>
        <p><strong>Date:</strong> ${new Date(sale.created_at).toLocaleString('fr-FR')}</p>
        <p><strong>Caissier:</strong> ${sale.cashier_name}</p>
        <p><strong>Client:</strong> ${sale.client_name || 'Client comptoir'}</p>
        ${sale.client_phone ? `<p><strong>Tél:</strong> ${sale.client_phone}</p>` : ''}
        ${sale.vehicle_plate ? `<p><strong>Véhicule:</strong> ${sale.vehicle_plate} ${sale.vehicle_model || ''}</p>` : ''}
      </div>
      <div class="line"></div>
      <div class="items">
        ${sale.items.map(item => `
          <div class="item">
            <div class="item-name">${item.description}</div>
            <div class="item-detail">
              <span>${item.quantity} x ${item.unit_price?.toFixed(2) || (item.total / item.quantity).toFixed(2)}€</span>
              <span>${item.total.toFixed(2)}€</span>
            </div>
          </div>
        `).join('')}
      </div>
      <div class="line"></div>
      <div class="subtotals">
        <div class="row"><span>Sous-total HT</span><span>${(sale.total / 1.2).toFixed(2)}€</span></div>
        <div class="row"><span>TVA 20%</span><span>${(sale.total - sale.total / 1.2).toFixed(2)}€</span></div>
      </div>
      <div class="double-line"></div>
      <div class="total">
        <span>TOTAL TTC</span>
        <span>${sale.total.toFixed(2)}€</span>
      </div>
      <div class="line"></div>
      <div class="payment">
        <p><strong>Règlement:</strong> ${sale.payment_method === 'cash' ? 'Espèces' : sale.payment_method === 'card' ? 'Carte bancaire' : sale.payment_method === 'check' ? 'Chèque' : sale.payment_method === 'transfer' ? 'Virement' : 'Mixte'}</p>
        ${sale.cash_amount > 0 ? `<p>Espèces: ${sale.cash_amount.toFixed(2)}€</p>` : ''}
        ${sale.card_amount > 0 ? `<p>CB: ${sale.card_amount.toFixed(2)}€</p>` : ''}
        ${sale.check_amount > 0 ? `<p>Chèque: ${sale.check_amount.toFixed(2)}€</p>` : ''}
        ${sale.transfer_amount > 0 ? `<p>Virement: ${sale.transfer_amount.toFixed(2)}€</p>` : ''}
        ${sale.cash_given ? `<p>Donné: ${sale.cash_given.toFixed(2)}€</p><p>Rendu: ${sale.cash_change.toFixed(2)}€</p>` : ''}
      </div>
      <div class="double-line"></div>
      <div class="footer">
        <p>Merci de votre visite !</p>
        <p>À très bientôt</p>
      </div>
      <div class="duplicata-footer">
        <p>DUPLICATA - Ne constitue pas une facture</p>
        <p>Édité le ${new Date().toLocaleString('fr-FR')}</p>
      </div>
      <script>
        window.onload = function() { 
          setTimeout(function() { window.print(); }, 100);
        }
      </script>
    </body>
    </html>
  `;
  safePrintOpen(htmlContent);
};

/**
 * Print sale invoice (full A4 format)
 * @param {Object} sale - Sale object from cash register
 */
export const printSaleInvoice = (sale) => {
  const invoiceNumber = sale.sale_number.replace('VENTE', 'FACT');
  const subtotalHT = sale.total / 1.2;
  const tvaAmount = sale.total - subtotalHT;
  
  const htmlContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Facture ${invoiceNumber}</title>
      <style>
        @page { size: A4; margin: 15mm; }
        * { box-sizing: border-box; }
        body { 
          font-family: 'Helvetica', Arial, sans-serif; 
          font-size: 11px;
          color: #333;
          line-height: 1.4;
          padding: 20px;
          max-width: 210mm;
          margin: 0 auto;
        }
        .header { display: flex; justify-content: space-between; margin-bottom: 30px; }
        .company { }
        .company h1 { color: #c00; font-size: 24px; margin: 0 0 5px 0; }
        .company p { margin: 2px 0; font-size: 10px; color: #666; }
        .invoice-info { text-align: right; }
        .invoice-info h2 { font-size: 28px; color: #333; margin: 0 0 10px 0; }
        .invoice-info p { margin: 3px 0; }
        .invoice-info .number { font-size: 14px; font-weight: bold; color: #c00; }
        .client-box { 
          background: #f5f5f5; 
          padding: 15px; 
          border-radius: 5px; 
          margin: 20px 0;
          width: 50%;
          margin-left: auto;
        }
        .client-box h3 { margin: 0 0 10px 0; font-size: 12px; color: #666; }
        .client-box p { margin: 3px 0; font-size: 12px; }
        table { width: 100%; border-collapse: collapse; margin: 20px 0; }
        th { background: #333; color: white; padding: 10px; text-align: left; font-size: 11px; }
        td { padding: 10px; border-bottom: 1px solid #ddd; font-size: 11px; }
        .qty { text-align: center; width: 60px; }
        .price { text-align: right; width: 100px; }
        .totals { margin-left: auto; width: 300px; }
        .totals table { margin: 0; }
        .totals td { border: none; padding: 5px 10px; }
        .totals .label { text-align: right; color: #666; }
        .totals .value { text-align: right; font-weight: bold; }
        .totals .grand-total td { font-size: 16px; background: #c00; color: white; }
        .payment-info { 
          margin-top: 30px; 
          padding: 15px; 
          background: #f0f7ff; 
          border-radius: 5px; 
        }
        .payment-info h4 { margin: 0 0 10px 0; color: #0066cc; }
        .footer { 
          margin-top: 40px; 
          padding-top: 20px; 
          border-top: 2px solid #333;
          font-size: 9px;
          color: #666;
          text-align: center;
        }
        @media print {
          body { padding: 0; }
          .no-print { display: none; }
        }
      </style>
    </head>
    <body>
      <div class="header">
        <div class="company">
          <img src="${COMPANY_INFO.logoUrl}" style="max-width: 150px; margin-bottom: 10px;" alt="Le Hub Atelier"/>
          <h1>LE HUB ATELIER</h1>
          <p><strong>${COMPANY_INFO.name}</strong></p>
          <p>142 boulevard Murat</p>
          <p>75016 Paris</p>
          <p>Tél: ${COMPANY_INFO.phone}</p>
          <p>Email: ${COMPANY_INFO.email}</p>
          <p>SIREN: ${COMPANY_INFO.siren}</p>
          <p>SIRET: ${COMPANY_INFO.siret}</p>
          <p>N° TVA: ${COMPANY_INFO.tva}</p>
        </div>
        <div class="invoice-info">
          <h2>FACTURE</h2>
          <p class="number">${invoiceNumber}</p>
          <p>Date: ${new Date(sale.created_at).toLocaleDateString('fr-FR')}</p>
          <p>Échéance: Comptant</p>
        </div>
      </div>
      
      <div class="client-box">
        <h3>FACTURER À</h3>
        <p><strong>${sale.client_name}</strong></p>
        ${sale.client_email ? `<p>Email: ${sale.client_email}</p>` : ''}
        ${sale.client_phone ? `<p>Tél: ${sale.client_phone}</p>` : ''}
        ${sale.vehicle_plate ? `<p>Véhicule: ${sale.vehicle_plate} ${sale.vehicle_model || ''} ${sale.vehicle_year || ''} ${sale.vehicle_color || ''}</p>` : ''}
      </div>

      <table>
        <thead>
          <tr>
            <th>Description</th>
            <th class="qty">Qté</th>
            <th class="price">P.U. HT</th>
            <th class="price">Total HT</th>
          </tr>
        </thead>
        <tbody>
          ${sale.items.map(item => {
            const unitHT = (item.unit_price || item.total / item.quantity) / 1.2;
            const totalHT = item.total / 1.2;
            return `
              <tr>
                <td>${item.description}</td>
                <td class="qty">${item.quantity}</td>
                <td class="price">${unitHT.toFixed(2)} €</td>
                <td class="price">${totalHT.toFixed(2)} €</td>
              </tr>
            `;
          }).join('')}
        </tbody>
      </table>

      <div class="totals">
        <table>
          <tr>
            <td class="label">Total HT</td>
            <td class="value">${subtotalHT.toFixed(2)} €</td>
          </tr>
          <tr>
            <td class="label">TVA 20%</td>
            <td class="value">${tvaAmount.toFixed(2)} €</td>
          </tr>
          <tr class="grand-total">
            <td class="label">TOTAL TTC</td>
            <td class="value">${sale.total.toFixed(2)} €</td>
          </tr>
        </table>
      </div>

      <div class="payment-info">
        <h4>Règlement</h4>
        <p><strong>Mode:</strong> ${sale.payment_method === 'cash' ? 'Espèces' : sale.payment_method === 'card' ? 'Carte bancaire' : sale.payment_method === 'check' ? 'Chèque' : sale.payment_method === 'transfer' ? 'Virement instantané' : 'Paiement mixte'}</p>
        ${sale.cash_amount > 0 ? `<p>Espèces: ${sale.cash_amount.toFixed(2)} €</p>` : ''}
        ${sale.card_amount > 0 ? `<p>Carte bancaire: ${sale.card_amount.toFixed(2)} €</p>` : ''}
        ${sale.check_amount > 0 ? `<p>Chèque ${sale.check_number ? `N° ${sale.check_number}` : ''}: ${sale.check_amount.toFixed(2)} €</p>` : ''}
        ${sale.transfer_amount > 0 ? `<p>Virement instantané: ${sale.transfer_amount.toFixed(2)} €</p>` : ''}
        <p><strong>Statut:</strong> <span style="color: green; font-weight: bold;">PAYÉ</span></p>
      </div>

      <div style="margin-top: 20px; padding: 15px; background: #f0f0f0; border-radius: 5px; font-size: 10px;">
        <strong style="color: #0066cc;">Coordonnées bancaires :</strong><br>
        IBAN: FR76 1010 7001 1100 3180 8375 451<br>
        BIC: BREDFRPPXXX
      </div>

      <div class="footer">
        <p>${COMPANY_INFO.name} - ${COMPANY_INFO.address}</p>
        <p>SIREN: ${COMPANY_INFO.siren} - SIRET: ${COMPANY_INFO.siret} - N° TVA: ${COMPANY_INFO.tva}</p>
        <p>En cas de retard de paiement, une pénalité de 3 fois le taux d'intérêt légal sera appliquée.</p>
        <p>Indemnité forfaitaire pour frais de recouvrement: 40€</p>
      </div>

      <script>
        window.onload = function() { 
          setTimeout(function() { window.print(); }, 200);
        }
      </script>
    </body>
    </html>
  `;
  safePrintOpen(htmlContent);
};

/**
 * Print client request sheet for quote requests
 * @param {Object} request - Quote request object
 */
export const printClientRequestSheet = (request) => {
  const htmlContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Fiche Demande - ${request.name}</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: Arial, sans-serif; padding: 30px; color: #333; background: white; }
        .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 30px; padding-bottom: 20px; border-bottom: 3px solid #dc2626; }
        .logo img { max-width: 150px; }
        .title { text-align: right; }
        .title h1 { color: #dc2626; font-size: 24px; margin-bottom: 5px; }
        .title p { color: #666; font-size: 12px; }
        .section { margin: 25px 0; padding: 20px; background: #f8f8f8; border-radius: 8px; border-left: 4px solid #dc2626; }
        .section h2 { color: #dc2626; font-size: 14px; text-transform: uppercase; margin-bottom: 15px; letter-spacing: 1px; }
        .grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 15px; }
        .field { margin-bottom: 10px; }
        .field label { display: block; font-size: 11px; color: #666; text-transform: uppercase; margin-bottom: 3px; }
        .field .value { font-size: 14px; font-weight: bold; color: #1a1a1a; padding: 8px 0; border-bottom: 1px solid #ddd; }
        .description { margin-top: 20px; padding: 20px; background: #fff3cd; border-radius: 8px; border: 1px solid #ffc107; }
        .description h3 { color: #856404; margin-bottom: 10px; font-size: 14px; }
        .description p { color: #333; line-height: 1.6; white-space: pre-wrap; }
        .attachments { margin-top: 20px; padding: 15px; background: #e8f4fd; border-radius: 8px; }
        .attachments h3 { color: #0056b3; margin-bottom: 10px; font-size: 14px; }
        .status { display: inline-block; padding: 5px 15px; border-radius: 20px; font-size: 12px; font-weight: bold; }
        .status.pending { background: #ffc107; color: #333; }
        .status.in_progress { background: #17a2b8; color: white; }
        .status.accepted { background: #28a745; color: white; }
        .status.rejected { background: #dc3545; color: white; }
        .footer { margin-top: 40px; padding-top: 20px; border-top: 2px solid #eee; text-align: center; font-size: 10px; color: #666; }
        .internal-note { margin-top: 30px; padding: 20px; border: 2px dashed #ccc; border-radius: 8px; }
        .internal-note h3 { color: #666; margin-bottom: 10px; }
        .internal-note .lines { height: 100px; background: repeating-linear-gradient(white, white 24px, #eee 25px); }
        @media print { body { padding: 15px; } }
      </style>
    </head>
    <body>
      <div class="header">
        <div class="logo">
          <img src="${COMPANY_INFO.logoUrl}" alt="Logo"/>
          <p style="font-size: 11px; color: #666; margin-top: 5px;">${COMPANY_INFO.name}</p>
        </div>
        <div class="title">
          <h1>FICHE DEMANDE DE DEVIS</h1>
          <p>Reçue le ${new Date(request.created_at).toLocaleDateString('fr-FR')} à ${new Date(request.created_at).toLocaleTimeString('fr-FR')}</p>
          <p style="margin-top: 10px;">
            <span class="status ${request.status}">${
              request.status === 'pending' ? '⏳ EN ATTENTE' :
              request.status === 'in_progress' ? '🔄 EN COURS' :
              request.status === 'accepted' ? '✅ ACCEPTÉ' :
              request.status === 'rejected' ? '❌ REFUSÉ' :
              request.status === 'quote_sent' ? '📩 DEVIS ENVOYÉ' :
              request.status.toUpperCase()
            }</span>
          </p>
        </div>
      </div>

      <div class="section">
        <h2>👤 Informations Client</h2>
        <div class="grid">
          <div class="field">
            <label>Nom complet</label>
            <div class="value">${request.name || 'Non renseigné'}</div>
          </div>
          <div class="field">
            <label>Email</label>
            <div class="value">${request.email || 'Non renseigné'}</div>
          </div>
          <div class="field">
            <label>Téléphone</label>
            <div class="value">${request.phone || 'Non renseigné'}</div>
          </div>
          <div class="field">
            <label>Adresse</label>
            <div class="value">${request.address || 'Non renseignée'}</div>
          </div>
        </div>
      </div>

      <div class="section">
        <h2>🚗 Informations Véhicule</h2>
        <div class="grid">
          <div class="field">
            <label>Marque</label>
            <div class="value">${request.vehicle_brand || 'Non renseignée'}</div>
          </div>
          <div class="field">
            <label>Modèle</label>
            <div class="value">${request.vehicle_model || 'Non renseigné'}</div>
          </div>
          <div class="field">
            <label>Immatriculation</label>
            <div class="value">${request.vehicle_plate || 'Non renseignée'}</div>
          </div>
          <div class="field">
            <label>Année</label>
            <div class="value">${request.vehicle_year || 'Non renseignée'}</div>
          </div>
        </div>
      </div>

      <div class="section">
        <h2>🔧 Service demandé</h2>
        <div class="field">
          <label>Type de prestation</label>
          <div class="value" style="font-size: 16px; color: #dc2626;">${request.service_type || 'Non spécifié'}</div>
        </div>
      </div>

      ${request.description ? `
        <div class="description">
          <h3>📝 Description de la demande</h3>
          <p>${request.description}</p>
        </div>
      ` : ''}

      ${request.attachments && request.attachments.length > 0 ? `
        <div class="attachments">
          <h3>📎 Pièces jointes (${request.attachments.length})</h3>
          <p style="font-size: 12px;">Le client a joint ${request.attachments.length} fichier(s) à sa demande.</p>
        </div>
      ` : ''}

      <div class="internal-note">
        <h3>📋 Notes internes (à compléter)</h3>
        <div class="lines"></div>
      </div>

      <div class="footer">
        <p>${COMPANY_INFO.name} - ${COMPANY_INFO.address}</p>
        <p>SIRET: ${COMPANY_INFO.siret} - TVA: ${COMPANY_INFO.tva} - Tél: ${COMPANY_INFO.phone}</p>
        <p style="margin-top: 10px;">Document généré le ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR')}</p>
      </div>

      <script>window.onload = function() { setTimeout(function() { window.print(); }, 300); }</script>
    </body>
    </html>
  `;
  safePrintOpen(htmlContent);
};

/**
 * Print professional quote
 * @param {Object} quote - Quote object
 */
export const printProfessionalQuote = (quote) => {
  const subtotalHT = quote.subtotal;
  const tva = quote.tax_amount;
  const total = quote.total;
  
  const htmlContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Devis ${quote.quote_number}</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        @page { size: A4; margin: 15mm; }
        body { font-family: 'Helvetica', Arial, sans-serif; font-size: 11px; color: #333; padding: 20px; }
        .header { display: flex; justify-content: space-between; margin-bottom: 25px; border-bottom: 3px solid #dc2626; padding-bottom: 15px; }
        .logo-section { }
        .logo-section img { max-height: 70px; }
        .logo-section p { font-size: 10px; color: #666; margin-top: 5px; }
        .quote-info { text-align: right; }
        .quote-info h1 { font-size: 28px; color: #dc2626; margin-bottom: 5px; }
        .quote-info .number { font-size: 14px; font-weight: bold; }
        .quote-info .date { font-size: 11px; color: #666; }
        .quote-info .validity { font-size: 11px; color: #dc2626; font-weight: bold; margin-top: 5px; }
        .addresses { display: flex; justify-content: space-between; margin: 25px 0; }
        .address { width: 45%; }
        .address h3 { font-size: 10px; color: #dc2626; text-transform: uppercase; margin-bottom: 8px; letter-spacing: 1px; }
        .address p { font-size: 11px; line-height: 1.5; }
        .vehicle-box { background: #f5f5f5; padding: 12px; border-radius: 5px; margin: 15px 0; }
        .vehicle-box h4 { font-size: 10px; color: #666; margin-bottom: 5px; }
        .vehicle-box p { font-size: 11px; }
        table { width: 100%; border-collapse: collapse; margin: 20px 0; }
        th { background: #333; color: white; padding: 10px; text-align: left; font-size: 10px; }
        td { padding: 10px; border-bottom: 1px solid #eee; font-size: 11px; }
        .text-right { text-align: right; }
        .text-center { text-align: center; }
        .totals { width: 280px; margin-left: auto; margin-top: 20px; }
        .totals table { margin: 0; }
        .totals td { padding: 6px 10px; border: none; }
        .totals .label { text-align: right; color: #666; }
        .totals .value { text-align: right; font-weight: bold; }
        .totals .total-row td { background: #dc2626; color: white; font-size: 14px; }
        .notes { background: #f9f9f9; padding: 15px; border-radius: 5px; margin: 20px 0; font-size: 10px; }
        .notes h4 { margin-bottom: 8px; color: #666; }
        .signature-box { margin-top: 40px; display: flex; justify-content: space-between; }
        .signature { width: 45%; border: 1px solid #ddd; padding: 15px; border-radius: 5px; }
        .signature h4 { font-size: 10px; color: #666; margin-bottom: 10px; }
        .signature .line { border-bottom: 1px solid #333; margin-top: 40px; }
        .signature p { font-size: 9px; color: #999; margin-top: 5px; }
        .conditions { margin-top: 30px; padding: 15px; background: #fff8e1; border-radius: 5px; font-size: 9px; }
        .conditions h4 { font-size: 10px; margin-bottom: 8px; }
        .footer { margin-top: 30px; padding-top: 15px; border-top: 2px solid #eee; text-align: center; font-size: 9px; color: #666; }
        @media print { body { padding: 0; } }
      </style>
    </head>
    <body>
      <div class="header">
        <div class="logo-section">
          <img src="${COMPANY_INFO.logoUrl}" alt="Logo" />
          <p>${COMPANY_INFO.name}</p>
        </div>
        <div class="quote-info">
          <h1>DEVIS</h1>
          <p class="number">${quote.quote_number}</p>
          <p class="date">Date: ${new Date(quote.created_at).toLocaleDateString('fr-FR')}</p>
          <p class="validity">Validité: ${new Date(quote.valid_until).toLocaleDateString('fr-FR')}</p>
        </div>
      </div>

      <div class="addresses">
        <div class="address">
          <h3>Émetteur</h3>
          <p>
            <strong>${COMPANY_INFO.name}</strong><br>
            ${COMPANY_INFO.address}<br>
            SIRET: ${COMPANY_INFO.siret}<br>
            TVA: ${COMPANY_INFO.tva}<br>
            Tél: ${COMPANY_INFO.phone}<br>
            ${COMPANY_INFO.email}
          </p>
        </div>
        <div class="address">
          <h3>Client</h3>
          <p>
            ${quote.client_company ? `<strong>${quote.client_company}</strong><br>` : ''}
            <strong>${quote.client_name}</strong><br>
            ${quote.client_address ? quote.client_address + '<br>' : ''}
            ${quote.client_phone ? `Tél: ${quote.client_phone}<br>` : ''}
            ${quote.client_email}
          </p>
        </div>
      </div>

      ${quote.vehicle_plate || quote.vehicle_brand ? `
        <div class="vehicle-box">
          <h4>🚗 VÉHICULE</h4>
          <p>
            ${quote.vehicle_brand || ''} ${quote.vehicle_model || ''} 
            ${quote.vehicle_year ? `(${quote.vehicle_year})` : ''} 
            ${quote.vehicle_plate ? `- Immat: <strong>${quote.vehicle_plate}</strong>` : ''}
          </p>
        </div>
      ` : ''}

      <table>
        <thead>
          <tr>
            <th style="width: 50%;">Description</th>
            <th class="text-center" style="width: 15%;">Quantité</th>
            <th class="text-right" style="width: 17%;">Prix unit. HT</th>
            <th class="text-right" style="width: 18%;">Total HT</th>
          </tr>
        </thead>
        <tbody>
          ${quote.items.map(item => `
            <tr>
              <td>${item.description}</td>
              <td class="text-center">${item.quantity}</td>
              <td class="text-right">${item.unit_price.toFixed(2)} €</td>
              <td class="text-right">${item.total.toFixed(2)} €</td>
            </tr>
          `).join('')}
        </tbody>
      </table>

      <div class="totals">
        <table>
          <tr>
            <td class="label">Total HT</td>
            <td class="value">${subtotalHT.toFixed(2)} €</td>
          </tr>
          <tr>
            <td class="label">TVA (20%)</td>
            <td class="value">${tva.toFixed(2)} €</td>
          </tr>
          <tr class="total-row">
            <td class="label">Total TTC</td>
            <td class="value">${total.toFixed(2)} €</td>
          </tr>
        </table>
      </div>

      ${quote.notes ? `
        <div class="notes">
          <h4>Notes / Conditions particulières</h4>
          <p>${quote.notes}</p>
        </div>
      ` : ''}

      <div class="signature-box">
        <div class="signature">
          <h4>Pour le prestataire</h4>
          <p>Date: ${new Date().toLocaleDateString('fr-FR')}</p>
          <div class="line"></div>
          <p>Signature et cachet</p>
        </div>
        <div class="signature">
          <h4>Bon pour accord - Le client</h4>
          <p>Date: _______________</p>
          <div class="line"></div>
          <p>Signature précédée de "Bon pour accord"</p>
        </div>
      </div>

      <div class="conditions">
        <h4>Conditions générales</h4>
        <p>• Ce devis est valable ${quote.validity_days || 30} jours à compter de sa date d'émission.</p>
        <p>• Acompte de 30% à la commande, solde à la livraison.</p>
        <p>• Délai d'exécution à convenir selon planning.</p>
        <p>• TVA non applicable, art. 293 B du CGI (si applicable), sinon TVA 20%.</p>
      </div>

      <div style="margin-top: 20px; padding: 15px; background: #f0f0f0; border-radius: 5px; font-size: 10px;">
        <strong style="color: #dc2626;">Coordonnées bancaires pour règlement :</strong><br>
        IBAN: FR76 1010 7001 1100 3180 8375 451<br>
        BIC: BREDFRPPXXX
      </div>

      <div class="footer">
        <p>${COMPANY_INFO.name} - ${COMPANY_INFO.address}</p>
        <p>SIRET: ${COMPANY_INFO.siret} - TVA: ${COMPANY_INFO.tva}</p>
        <p>Tél: ${COMPANY_INFO.phone} - ${COMPANY_INFO.email}</p>
      </div>

      <script>window.onload = function() { setTimeout(function() { window.print(); }, 300); }</script>
    </body>
    </html>
  `;
  safePrintOpen(htmlContent);
};

/**
 * Print contact message with client info
 * @param {Object} message - Contact message object
 */
export const printContactMessage = (message) => {
  const htmlContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Message - ${message.name}</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: Arial, sans-serif; padding: 30px; color: #333; background: white; }
        .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 30px; padding-bottom: 20px; border-bottom: 3px solid #dc2626; }
        .logo img { max-width: 150px; }
        .title { text-align: right; }
        .title h1 { color: #dc2626; font-size: 24px; margin-bottom: 5px; }
        .title p { color: #666; font-size: 12px; }
        .section { margin: 25px 0; padding: 20px; background: #f8f8f8; border-radius: 8px; border-left: 4px solid #dc2626; }
        .section h2 { color: #dc2626; font-size: 14px; text-transform: uppercase; margin-bottom: 15px; letter-spacing: 1px; }
        .grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 15px; }
        .field { margin-bottom: 10px; }
        .field label { display: block; font-size: 11px; color: #666; text-transform: uppercase; margin-bottom: 3px; }
        .field .value { font-size: 14px; font-weight: bold; color: #1a1a1a; padding: 8px 0; border-bottom: 1px solid #ddd; }
        .message-box { margin-top: 20px; padding: 20px; background: #fff3cd; border-radius: 8px; border: 1px solid #ffc107; }
        .message-box h3 { color: #856404; margin-bottom: 10px; font-size: 14px; }
        .message-box .subject { color: #dc2626; font-weight: bold; font-size: 16px; margin-bottom: 15px; padding-bottom: 10px; border-bottom: 1px solid #ffc107; }
        .message-box .content { color: #333; line-height: 1.8; white-space: pre-wrap; font-size: 14px; }
        .status { display: inline-block; padding: 5px 15px; border-radius: 20px; font-size: 12px; font-weight: bold; }
        .status.unread { background: #dc3545; color: white; }
        .status.read { background: #6c757d; color: white; }
        .footer { margin-top: 40px; padding-top: 20px; border-top: 2px solid #eee; text-align: center; font-size: 10px; color: #666; }
        .response-section { margin-top: 30px; padding: 20px; border: 2px dashed #ccc; border-radius: 8px; }
        .response-section h3 { color: #666; margin-bottom: 10px; }
        .response-section .lines { height: 150px; background: repeating-linear-gradient(white, white 24px, #eee 25px); }
        @media print { body { padding: 15px; } }
      </style>
    </head>
    <body>
      <div class="header">
        <div class="logo">
          <img src="${COMPANY_INFO.logoUrl}" alt="Logo"/>
          <p style="font-size: 11px; color: #666; margin-top: 5px;">${COMPANY_INFO.name}</p>
        </div>
        <div class="title">
          <h1>FICHE MESSAGE</h1>
          <p>Reçu le ${new Date(message.created_at).toLocaleDateString('fr-FR')} à ${new Date(message.created_at).toLocaleTimeString('fr-FR')}</p>
          <p style="margin-top: 10px;">
            <span class="status ${message.status}">${message.status === 'unread' ? '📩 NON LU' : '✓ LU'}</span>
          </p>
        </div>
      </div>

      <div class="section">
        <h2>👤 Informations Contact</h2>
        <div class="grid">
          <div class="field">
            <label>Nom complet</label>
            <div class="value">${message.name || 'Non renseigné'}</div>
          </div>
          <div class="field">
            <label>Email</label>
            <div class="value">${message.email || 'Non renseigné'}</div>
          </div>
          <div class="field">
            <label>Téléphone</label>
            <div class="value">${message.phone || 'Non renseigné'}</div>
          </div>
          <div class="field">
            <label>Date de réception</label>
            <div class="value">${new Date(message.created_at).toLocaleString('fr-FR')}</div>
          </div>
        </div>
      </div>

      <div class="message-box">
        <h3>📝 Message</h3>
        <div class="subject">Sujet: ${message.subject || 'Sans objet'}</div>
        <div class="content">${message.message || 'Aucun contenu'}</div>
      </div>

      <div class="response-section">
        <h3>📋 Réponse / Notes internes (à compléter)</h3>
        <div class="lines"></div>
      </div>

      <div class="footer">
        <p>${COMPANY_INFO.name} - ${COMPANY_INFO.address}</p>
        <p>SIRET: ${COMPANY_INFO.siret} - TVA: ${COMPANY_INFO.tva} - Tél: ${COMPANY_INFO.phone}</p>
        <p style="margin-top: 10px;">Document généré le ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR')}</p>
      </div>

      <script>window.onload = function() { setTimeout(function() { window.print(); }, 300); }</script>
    </body>
    </html>
  `;
  safePrintOpen(htmlContent);
};



/**
 * Print client sheet from appointment with history
 * @param {Object} appointment - Appointment object with all details
 * @param {Object} history - Optional client history object
 */
export const printAppointmentClientSheet = (appointment, history = null) => {
  const logoUrl = COMPANY_INFO.logoUrl;
  
  // Generate history HTML if available
  let historyHTML = '';
  if (history && history.appointments && history.appointments.length > 1) {
    const previousAppointments = history.appointments.filter(a => a.id !== appointment.id);
    if (previousAppointments.length > 0) {
      historyHTML = `
        <div class="section history-section">
          <h3>Historique des visites (${previousAppointments.length} visite${previousAppointments.length > 1 ? 's' : ''} précédente${previousAppointments.length > 1 ? 's' : ''})</h3>
          <table class="history-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Service</th>
                <th>Véhicule</th>
                <th>Statut</th>
              </tr>
            </thead>
            <tbody>
              ${previousAppointments.slice(0, 10).map(apt => `
                <tr>
                  <td>${new Date(apt.date).toLocaleDateString('fr-FR')}</td>
                  <td>${apt.service_name || '-'}</td>
                  <td>${apt.vehicle_brand || ''} ${apt.vehicle_model || ''} ${apt.vehicle_plate ? `(${apt.vehicle_plate})` : ''}</td>
                  <td><span class="status-mini status-${apt.status}">${apt.status === 'completed' ? 'Terminé' : apt.status === 'confirmed' ? 'Confirmé' : apt.status === 'pending' ? 'En attente' : apt.status}</span></td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
      `;
    }
  }
  
  // Generate sales history if available
  let salesHistoryHTML = '';
  if (history && history.sales && history.sales.length > 0) {
    salesHistoryHTML = `
      <div class="section">
        <h3>Historique des achats en caisse (${history.sales.length})</h3>
        <table class="history-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Prestations</th>
              <th>Montant</th>
              <th>Paiement</th>
            </tr>
          </thead>
          <tbody>
            ${history.sales.slice(0, 10).map(sale => `
              <tr>
                <td>${new Date(sale.created_at).toLocaleDateString('fr-FR')}</td>
                <td>${sale.items ? sale.items.map(i => i.description).join(', ') : '-'}</td>
                <td><strong>${sale.total?.toFixed(2) || 0}€</strong></td>
                <td>${sale.payment_method === 'cash' ? 'Espèces' : sale.payment_method === 'card' ? 'CB' : sale.payment_method}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        ${history.stats ? `<p class="total-spent">Total dépensé : <strong>${history.stats.total_spent?.toFixed(2) || 0}€</strong></p>` : ''}
      </div>
    `;
  }
  
  const clientSheetHTML = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Fiche Client - ${appointment.user_name}</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: 'Segoe UI', Arial, sans-serif; padding: 30px; color: #333; background: #fff; }
        .header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 30px; border-bottom: 3px solid #dc2626; padding-bottom: 20px; }
        .logo-img { height: 80px; }
        .doc-title { text-align: right; }
        .doc-title h1 { font-size: 28px; color: #dc2626; margin-bottom: 5px; }
        .doc-title h2 { font-size: 18px; color: #333; font-weight: normal; }
        .doc-title p { color: #666; font-size: 12px; margin-top: 5px; }
        .section { margin-bottom: 25px; padding: 20px; border: 1px solid #e5e7eb; border-radius: 8px; }
        .section h3 { color: #dc2626; margin-bottom: 15px; font-size: 14px; text-transform: uppercase; letter-spacing: 1px; border-bottom: 2px solid #dc2626; padding-bottom: 5px; }
        .info-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 15px; }
        .info-grid-3 { display: grid; grid-template-columns: repeat(3, 1fr); gap: 15px; }
        .info-item { }
        .info-item label { display: block; font-size: 11px; color: #666; text-transform: uppercase; margin-bottom: 3px; }
        .info-item .value { font-size: 14px; font-weight: 500; color: #111; }
        .info-item .value.large { font-size: 18px; color: #dc2626; }
        .highlight { background: #fef2f2; border-color: #dc2626; }
        .status-badge { display: inline-block; padding: 5px 15px; border-radius: 20px; font-size: 12px; font-weight: bold; }
        .status-pending { background: #fef3c7; color: #92400e; }
        .status-confirmed { background: #d1fae5; color: #065f46; }
        .status-completed { background: #dbeafe; color: #1e40af; }
        .status-cancelled { background: #fee2e2; color: #991b1b; }
        .status-mini { padding: 2px 8px; border-radius: 10px; font-size: 10px; }
        .history-table { width: 100%; border-collapse: collapse; margin-top: 10px; font-size: 12px; }
        .history-table th, .history-table td { padding: 8px; text-align: left; border-bottom: 1px solid #e5e7eb; }
        .history-table th { background: #f9fafb; font-weight: 600; color: #374151; }
        .history-table tr:hover { background: #f9fafb; }
        .total-spent { margin-top: 15px; text-align: right; font-size: 14px; }
        .notes-section { margin-top: 20px; }
        .notes-lines { min-height: 80px; border: 1px dashed #ccc; border-radius: 5px; padding: 10px; background: #fafafa; }
        .notes-lines::before { content: "Notes techniques / Observations :"; display: block; color: #999; font-size: 11px; font-style: italic; margin-bottom: 10px; }
        .footer { margin-top: 30px; text-align: center; font-size: 10px; color: #666; border-top: 1px solid #e5e7eb; padding-top: 15px; }
        .company-info { background: #1f2937; color: white; padding: 15px; border-radius: 8px; margin-bottom: 20px; }
        .company-info h4 { color: #dc2626; margin-bottom: 5px; }
        .company-info p { font-size: 11px; margin: 2px 0; }
        @media print { 
          body { padding: 15px; }
          .section { page-break-inside: avoid; }
        }
      </style>
    </head>
    <body>
      <div class="header">
        <img src="${logoUrl}" alt="Le Hub Atelier" class="logo-img" />
        <div class="doc-title">
          <h1>FICHE CLIENT</h1>
          <h2>${appointment.user_name}</h2>
          <p>Document généré le ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR')}</p>
        </div>
      </div>
      
      <div class="company-info">
        <h4>LE HUB ATELIER AUTO MOTO</h4>
        <p>${COMPANY_INFO.address}</p>
        <p>Tél: ${COMPANY_INFO.phone} | Email: info@lehubatelier.fr</p>
        <p>SIRET: ${COMPANY_INFO.siret} | TVA: ${COMPANY_INFO.tva}</p>
      </div>

      <div class="section">
        <h3>Informations client</h3>
        <div class="info-grid">
          <div class="info-item">
            <label>Nom complet</label>
            <div class="value large">${appointment.user_name || 'Non renseigné'}</div>
          </div>
          <div class="info-item">
            <label>Email</label>
            <div class="value">${appointment.user_email || 'Non renseigné'}</div>
          </div>
          <div class="info-item">
            <label>Téléphone</label>
            <div class="value">${appointment.user_phone || 'Non renseigné'}</div>
          </div>
          <div class="info-item">
            <label>ID Client</label>
            <div class="value" style="font-family: monospace; font-size: 10px;">${appointment.user_id || 'N/A'}</div>
          </div>
        </div>
      </div>

      <div class="section highlight">
        <h3>Rendez-vous actuel - ${new Date(appointment.date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</h3>
        <div class="info-grid-3">
          <div class="info-item">
            <label>Service demandé</label>
            <div class="value">${appointment.service_name || 'Non spécifié'}</div>
          </div>
          <div class="info-item">
            <label>Créneau horaire</label>
            <div class="value">${appointment.time_slot || 'Non spécifié'}</div>
          </div>
          <div class="info-item">
            <label>Statut</label>
            <div class="value">
              <span class="status-badge status-${appointment.status}">${appointment.status === 'pending' ? 'En attente' : appointment.status === 'confirmed' ? 'Confirmé' : appointment.status === 'completed' ? 'Terminé' : appointment.status === 'cancelled' ? 'Annulé' : appointment.status}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="section">
        <h3>Véhicule</h3>
        <div class="info-grid">
          <div class="info-item">
            <label>Marque</label>
            <div class="value">${appointment.vehicle_brand || 'Non renseigné'}</div>
          </div>
          <div class="info-item">
            <label>Modèle</label>
            <div class="value">${appointment.vehicle_model || 'Non renseigné'}</div>
          </div>
          <div class="info-item">
            <label>Immatriculation</label>
            <div class="value large" style="font-family: monospace;">${appointment.vehicle_plate || 'Non renseigné'}</div>
          </div>
          <div class="info-item">
            <label>Type</label>
            <div class="value">${appointment.vehicle_type === 'moto' ? 'Moto' : 'Voiture'}</div>
          </div>
        </div>
      </div>

      ${appointment.notes ? `
      <div class="section">
        <h3>Notes du client</h3>
        <p style="font-style: italic; color: #555; line-height: 1.6; padding: 10px; background: #f9fafb; border-radius: 5px;">"${appointment.notes}"</p>
      </div>
      ` : ''}

      ${historyHTML}
      
      ${salesHistoryHTML}

      <div class="notes-section">
        <div class="notes-lines"></div>
      </div>

      <div class="footer">
        <p><strong>${COMPANY_INFO.name}</strong> - Centre Auto & Moto Premium Paris 16</p>
        <p>${COMPANY_INFO.address}</p>
        <p>SIRET: ${COMPANY_INFO.siret} | TVA: ${COMPANY_INFO.tva} | Tél: ${COMPANY_INFO.phone}</p>
      </div>

      <script>window.onload = function() { setTimeout(function() { window.print(); }, 300); }</script>
    </body>
    </html>
  `;
  safePrintOpen(clientSheetHTML);
};
